import { useQuery } from "@tanstack/react-query";
import { Sparkles, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/cn";
import {
  listAutomationRuns, dashboardKeys, type AutomationRun,
} from "@/lib/dashboard-api";
import { DateCell } from "@/components/DateCell";

const STATUS_ICON: Record<AutomationRun['status'], React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  partial: AlertTriangle,
  error: XCircle,
};
const STATUS_TINT: Record<AutomationRun['status'], string> = {
  success: 'text-emerald-500',
  partial: 'text-amber-500',
  error: 'text-red-500',
};

export function AutomatedActivityCard() {
  const { sessionId } = useAuth();
  const { data: runs = [] } = useQuery({
    queryKey: [...dashboardKeys.automation(), sessionId],
    queryFn: () => listAutomationRuns(20, sessionId),
    enabled: !!sessionId,
    refetchInterval: 60_000,
  });

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500" />
          Automated activity
        </h2>
        <span className="text-xs text-muted-foreground">
          Last {runs.length}
        </span>
      </div>
      <div className="p-4">
        {runs.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6">
            No automated runs yet. Opt an account into the lights-out lane to see activity here.
          </div>
        ) : (
          <ul className="divide-y">
            {runs.map((r) => {
              const Icon = STATUS_ICON[r.status];
              return (
                <li key={r.id} className="py-2 flex items-start gap-3">
                  <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", STATUS_TINT[r.status])} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {r.summary || r.kind}
                    </p>
                    {r.message && (
                      <p className="text-xs text-muted-foreground truncate">{r.message}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    <DateCell value={r.createdAt} />
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
